import React, { useEffect, useState } from 'react'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Link,
  Spinner,
  Stack,
  Text
} from '@chakra-ui/react'
import startCase from 'lodash/startCase'

import InfoHint from '../components/InfoHint'
import {
  ShipStatusResponse,
  formatDisplayDate,
  isInCurrentMonth,
  isLastDayOfMonth,
  isSameMonthOrAfter
} from '../types'

type Props = {
  shipStatus: ShipStatusResponse | null
  statementEndDate: string
  isLoading: boolean
  onRefresh: () => Promise<void> | void
  onStatementEndDateChange: (value: string) => void
}

const DBT_HOURLY_WORKFLOW_URL =
  'https://console.cloud.google.com/workflows/workflow/us-central1/dbt-hourly-jobs-now-nightly/executions?project=homevest-production'

const VerifyDataFreshness = ({
  shipStatus,
  statementEndDate,
  isLoading,
  onRefresh,
  onStatementEndDateChange
}: Props) => {
  const [draftStatementEndDate, setDraftStatementEndDate] = useState(statementEndDate)
  const [statementEndDateError, setStatementEndDateError] = useState('')
  const isFresh =
    !!shipStatus?.maxStatementDate &&
    isSameMonthOrAfter(shipStatus.maxStatementDate, statementEndDate)
  const alreadyShippedThisMonth = isInCurrentMonth(shipStatus?.lastShipDate)

  useEffect(() => {
    setDraftStatementEndDate(statementEndDate)
  }, [statementEndDate])

  const onStatementDateInputChange = (value: string) => {
    setDraftStatementEndDate(value)

    if (!value) {
      setStatementEndDateError('Statement End Date is required.')
      onStatementEndDateChange(value)
      return
    }

    if (!isLastDayOfMonth(value)) {
      setStatementEndDateError('Statement End Date must be the last day of a month.')
      return
    }

    setStatementEndDateError('')
    onStatementEndDateChange(value)
  }

  return (
    <Stack spacing={3}>
      <FormControl maxW='280px' isInvalid={Boolean(statementEndDateError)}>
        <FormLabel>
          Statement End Date
          <InfoHint label='This is the statement month you intend to ship. Most downstream checks and send actions use this value as the target month context. Only month-end dates are allowed.' />
        </FormLabel>
        <Input
          type='date'
          value={draftStatementEndDate}
          onChange={(event) => onStatementDateInputChange(event.target.value)}
        />
        <FormErrorMessage>{statementEndDateError}</FormErrorMessage>
      </FormControl>

      <HStack spacing={3}>
        <Button colorScheme='teal' variant='outline' onClick={onRefresh} isDisabled={isLoading}>
          Refresh Ship Status
        </Button>
        <InfoHint label='Refresh reads the latest staging max date and statement_configurations values from backend APIs so you can re-check readiness before proceeding.' />
        {isLoading && <Spinner size='sm' />}
      </HStack>

      <Text>
        Staging Max Statement Date:{' '}
        <Text as='span' fontWeight='bold'>
          {formatDisplayDate(shipStatus?.maxStatementDate)}
        </Text>
        <InfoHint label='Source: BigQuery table dbt_prod.monthly_customer_statements. This is MAX(date) and indicates how recent statement calculation outputs are in staging.' />
      </Text>
      <Text>
        Current {startCase('app_end_date')}:{' '}
        <Text as='span' fontWeight='bold'>
          {formatDisplayDate(shipStatus?.appEndDate)}
        </Text>
        <InfoHint label='Source: Postgres statement_configurations.app_end_date. This tracks the most recently shipped statement month.' />
      </Text>
      <Text>
        Current {startCase('last_ship_date')}:{' '}
        <Text as='span' fontWeight='bold'>
          {formatDisplayDate(shipStatus?.lastShipDate)}
        </Text>
        <InfoHint label='Source: Postgres statement_configurations.last_ship_date. This stores when statements were most recently sent and is used for adjustment/snapshot context.' />
      </Text>

      {alreadyShippedThisMonth && (
        <Alert status='warning'>
          <AlertIcon />
          <Stack>
            <AlertTitle>Already shipped this month</AlertTitle>
            <AlertDescription>
              {startCase('last_ship_date')} is in the current month. Later shipping steps are
              locked.
            </AlertDescription>
          </Stack>
        </Alert>
      )}

      <Alert status='info'>
        <AlertIcon />
        <Stack>
          <AlertTitle>Shipping multiple months?</AlertTitle>
          <AlertDescription>
            If you are shipping statements for multiple months at once, make sure the{' '}
            <Link
              href={DBT_HOURLY_WORKFLOW_URL}
              isExternal
              color='blue.600'
              textDecoration='underline'
            >
              dbt hourly Workflow
            </Link>{' '}
            has run with mode: production between each statement run.
          </AlertDescription>
        </Stack>
      </Alert>

      {!isFresh && (
        <Alert status='warning'>
          <AlertIcon />
          <Stack>
            <AlertTitle>Data freshness warning</AlertTitle>
            <AlertDescription>
              Staging data is not in the same month as (or later than) your selected Statement End
              Date. Check{' '}
              <Link
                href={DBT_HOURLY_WORKFLOW_URL}
                isExternal
                color='blue.600'
                textDecoration='underline'
              >
                dbt hourly Workflow
              </Link>{' '}
              has run with mode: production and check the GL Transactions job.
            </AlertDescription>
          </Stack>
        </Alert>
      )}
      {isFresh && (
        <Alert status='success'>
          <AlertIcon />
          <AlertDescription>
            Staging data is current enough for the selected statement month.
          </AlertDescription>
        </Alert>
      )}
    </Stack>
  )
}

export default VerifyDataFreshness
